import * as React from 'react';
import noop from 'lodash/noop';

import { Form, FormField } from 'components/form';
import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';
import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';

import { __t, applyTranslations } from 'utils/language';
import { isNotEmpty, isRequired } from 'utils/validators';
import { MODAL_META_CHAR_LIMITS, UNTITLED } from 'modules/insights/sov/utils/sov';

import './sov-modal-meta.scss';

import type { TextEntry } from 'types';
import type {
  SOVGeneralProps,
  SOVReport,
  SOVReportType
} from 'modules/insights/sov/types';

import type { KeyboardEvent, SyntheticEvent } from 'react';

applyTranslations(require('locales/common/collabary-common.i18n.json'));

type Props = {
  report?: SOVReport,
  reportType: SOVReportType,
  title: string,
  labels: {
    name: string,
    description: string,
    actionButton: string
  },
  placeholders: {
    name: string,
    description: string
  },
  onAction: (a: SOVGeneralProps) => void,
  onCloseModal: () => void
};

type State = {
  name: string,
  description: string
};

const COLLABARY_MSG_KEY = 'collabary_common';

class SOVModalMeta extends React.PureComponent<Props, State> {
  state = {
    name: this.props.report?.name || UNTITLED,
    description: this.props.report?.description || ''
  };

  inputFieldRef: TextEntry | undefined | null = null;

  componentDidMount() {
    if (this.inputFieldRef) {
      this.inputFieldRef.select();
    }
  }

  // prevent line breaks in `description` field
  handleKeyPress(event: KeyboardEvent<TextEntry>): void {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  handleFieldChange = (event: SyntheticEvent<TextEntry>): void => {
    const target = event.currentTarget;
    const key = target.name || '';
    const value = target.value || '';

    if (key) {
      this.setState<any>({
        [key]: value
      });
    }
  };

  handleAction = (): void => {
    const { report, reportType, onAction, onCloseModal } = this.props;
    const { name, description } = this.state;
    const reportId = report?.id || 0;

    onAction({ reportId, reportType, name, description });
    onCloseModal();
  };

  renderFooter(): React.ReactNode {
    const { labels, onCloseModal } = this.props;
    const { name, description } = this.state;

    return (
      <ModalFrameFooter>
        <div className="sov-modal-meta__footer">
          <ActionButton
            color="tertiary"
            onClick={onCloseModal}
          >
            {__t(`${COLLABARY_MSG_KEY}.action_button.cancel`)}
          </ActionButton>
          <ActionButton
            type="submit"
            disabled={name.length < 1 || description.length > MODAL_META_CHAR_LIMITS.description}
          >
            {labels.actionButton}
          </ActionButton>
        </div>
      </ModalFrameFooter>
    );
  }

  renderInput(type: 'name' | 'description', value: string): React.ReactNode {
    const { placeholders } = this.props;
    const isName = type === 'name';
    const isDescription = type === 'description';

    return (
      <Input
        refCallback={isName ? (ref) => { this.inputFieldRef = ref; } : noop}
        type="text"
        name={type}
        value={value}
        placeholder={placeholders[type]}
        width="responsive"
        maxLength={MODAL_META_CHAR_LIMITS[type]}
        autoFocus={isName}
        multiline={isDescription}
        charCount={{ ...(isDescription ? {
          curCount: value.length,
          maxChars: MODAL_META_CHAR_LIMITS[type]
        } : null) }}
        {...(isDescription ? { onKeyDown: this.handleKeyPress } : null)}
        onChange={this.handleFieldChange}
      />
    );
  }

  renderName(): React.ReactNode {
    const { labels } = this.props;
    const { name } = this.state;

    return (
      <FormField
        direction="column"
        label={labels.name}
        labelType="highlighted"
        fontSize="small"
        showValidationIcon={false}
        showRequiredMark
        validators={[
          isNotEmpty(name),
          isRequired(name)
        ]}
      >
        {this.renderInput('name', name)}
      </FormField>
    );
  }

  renderDescription(): React.ReactNode {
    const { labels } = this.props;
    const { description } = this.state;

    return (
      <FormField
        direction="column"
        label={labels.description}
        labelType="highlighted"
        fontSize="small"
        showValidationIcon={false}
      >
        {this.renderInput('description', description)}
      </FormField>
    );
  }

  render(): React.ReactNode {
    const { title } = this.props;

    return (
      <ModalFrame title={title}>
        <ModalFrameInner
          narrowContent
        >
          <Form
            className="sov-modal-meta"
            onSubmit={this.handleAction}
          >
            {this.renderName()}
            {this.renderDescription()}
            {this.renderFooter()}
          </Form>
        </ModalFrameInner>
      </ModalFrame>
    );
  }
}

export default SOVModalMeta;
